import { useState, type KeyboardEvent } from 'react'
import { Input } from '../ui/controls'

/**
 * Ввод набора значений атрибута.
 *
 * Атрибут вида «несколько из списка» описывает свойство, которых у товара
 * бывает сразу несколько: встроенная защита прикрывает и локти, и плечи,
 * и спину. Каждое значение отбирается фильтром витрины самостоятельно,
 * поэтому вводятся они по отдельности, а не одной строкой через запятую.
 *
 * Запятая и Enter завершают значение — так набор вводится подряд, не
 * отрываясь от клавиатуры. Backspace в пустом поле снимает последнее
 * значение: это привычно по полям адресатов в почте.
 */
export default function ValueSetInput({
  id,
  values,
  onChange,
  disabled,
  invalid,
}: {
  id: string
  values: string[]
  onChange: (next: string[]) => void
  disabled?: boolean
  invalid?: boolean
}) {
  const [draft, setDraft] = useState('')

  function commit(text: string) {
    const added = text
      .split(',')
      .map((v) => v.trim())
      .filter((v) => v !== '' && !values.includes(v))
    if (added.length > 0) onChange([...values, ...added])
    setDraft('')
  }

  function onKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault()
      commit(draft)
      return
    }
    if (e.key === 'Backspace' && draft === '' && values.length > 0) {
      onChange(values.slice(0, -1))
    }
  }

  return (
    <div
      className={`flex flex-wrap items-center gap-1.5 rounded-md border bg-white px-2 py-1.5 dark:bg-ink-950 ${
        invalid ? 'border-warning-500' : 'border-ink-300 dark:border-ink-700'
      } ${disabled ? 'opacity-60' : 'focus-within:border-brand-500'}`}
    >
      {values.map((v) => (
        <span
          key={v}
          className="flex items-center gap-1 rounded bg-ink-100 py-0.5 pl-2 pr-1 text-sm dark:bg-ink-800"
        >
          {v}
          <button
            type="button"
            onClick={() => onChange(values.filter((x) => x !== v))}
            disabled={disabled}
            aria-label={`Убрать «${v}»`}
            className="rounded px-1 text-ink-400 hover:bg-ink-200 hover:text-ink-700 dark:hover:text-ink-200 dark:hover:bg-ink-700"
          >
            ×
          </button>
        </span>
      ))}
      <Input
        id={id}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={onKeyDown}
        onBlur={() => commit(draft)}
        disabled={disabled}
        placeholder={values.length === 0 ? 'значение и Enter' : ''}
        className="min-w-32 flex-1 border-0 bg-transparent px-1 py-0 focus:ring-0"
      />
    </div>
  )
}
